/**
 * The first thing a device shows: a grown-up logs in, and that is the setup
 * ([D-177](../../../docs/DECISIONS.md#d-177)).
 *
 * Logging in on a device is what signs it in. Behind the login this screen asks
 * the server for a device credential and keeps it in this browser, so the
 * children never see a login again: next time the app opens on their faces.
 * A family with no child yet is asked for one name, once, on the way.
 *
 * There is nothing to copy between screens. The device credential still exists,
 * because it is what lets a child in without a password, but nobody sees it.
 */

import { AVATAR_SPECIES } from '@capytalk/core';
import { useMemo, useRef, useState } from 'react';
import { type Client, createClient } from './api.js';
import { rememberDevice } from './device.js';
import { SignInOrUp } from './GrownUps.js';
import Sneakers from './Sneakers.js';

/** A name for the device list that a grown-up will recognise. */
export function deviceLabel(agent: string): string {
  if (/iPad/.test(agent)) return 'iPad';
  if (/iPhone/.test(agent)) return 'iPhone';
  if (/Android/.test(agent))
    return /Mobile/.test(agent) ? 'Android phone' : 'Android tablet';
  if (/Macintosh/.test(agent)) return 'Mac';
  if (/Windows/.test(agent)) return 'Windows computer';
  if (/CrOS/.test(agent)) return 'Chromebook';
  return 'This browser';
}

export default function Setup({
  onReady,
  build = createClient,
}: {
  /** The device is signed in and remembered; show the children's faces. */
  readonly onReady: () => void;
  /** How to build the API client. Injected by tests; nothing else passes it. */
  readonly build?: (token: () => string | null) => Client;
}) {
  const [token, setToken] = useState<string | null>(null);
  const [askName, setAskName] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(false);
  const held = useRef<string | null>(null);
  held.current = token;
  const client = useMemo(() => build(() => held.current), [build]);

  async function signInDevice(learnerIds: readonly string[]) {
    const made = await client.owner.addDevice.mutate({
      label: deviceLabel(globalThis.navigator?.userAgent ?? ''),
      learnerIds: [...learnerIds],
    });
    rememberDevice({ deviceId: made.deviceId, credential: made.credential });
    onReady();
  }

  async function afterLogin(next: string) {
    held.current = next;
    setToken(next);
    setBusy(true);
    setProblem(false);
    try {
      const kids = await client.owner.children.query();
      if (kids.length === 0) setAskName(true);
      else await signInDevice(kids.map((kid) => kid.learnerId));
    } catch {
      setProblem(true);
    } finally {
      setBusy(false);
    }
  }

  async function addFirstChild() {
    setBusy(true);
    setProblem(false);
    try {
      const child = await client.owner.add.mutate({
        displayName: name.trim(),
        avatarSpecies: AVATAR_SPECIES[0],
        avatarBackground: '#eaf2ff',
      });
      await signInDevice([child.learnerId]);
    } catch {
      setProblem(true);
    } finally {
      setBusy(false);
    }
  }

  const trouble = problem && (
    <p className="sub" role="alert" style={{ marginTop: 10 }}>
      Something went wrong. Try again.
    </p>
  );

  if (token === null) {
    return (
      <div className="frame">
        <div className="center pad" style={{ paddingTop: 18 }}>
          <Sneakers
            text="Hi! I'm Sneakers. I'm going to help you learn a new language."
            audioRef={null}
            size={110}
          />
        </div>
        <SignInOrUp
          client={client}
          onSignedIn={(next) => {
            void afterLogin(next);
          }}
        />
      </div>
    );
  }

  if (askName) {
    return (
      <div className="frame">
        <form
          className="pad"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '') void addFirstChild();
          }}
        >
          <h2>Who is learning?</h2>
          <p className="sub">
            Your child’s first name. You can add more children later.
          </p>
          <input
            className="chip"
            style={{ width: '100%', marginTop: 12 }}
            aria-label="The child’s name"
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          {trouble}
          <button
            className="btn"
            type="submit"
            disabled={busy || name.trim() === ''}
            style={{ marginTop: 14 }}
          >
            {busy ? 'One moment…' : 'Continue'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="frame">
      <div className="pad">
        {problem ? (
          <>
            {trouble}
            <button
              className="btn"
              type="button"
              style={{ marginTop: 14 }}
              onClick={() => {
                void afterLogin(token);
              }}
            >
              Try again
            </button>
          </>
        ) : (
          <p className="sub" role="status">
            One moment…
          </p>
        )}
      </div>
    </div>
  );
}
